import { reactive, ref } from 'vue';
import { api } from '../lib/api';

export type AppUser = { id: string; username: string; role: 'admin' | 'member' };

export function useSessionController(options: { initializeWorkspace: () => Promise<void>; clearWorkspace: () => void }) {
  const authReady = ref(false);
  const currentUser = ref<AppUser | null>(null);
  const loginForm = reactive({ username: '', password: '' });
  const loginError = ref('');
  const loginPending = ref(false);
  const usersOpen = ref(false);
  const managedUsers = ref<Array<AppUser & { createdAt?: string }>>([]);
  const newUserForm = reactive({ username: '', password: '', role: 'member' as AppUser['role'] });
  const userManagementError = ref('');
  let generation = 0;

  async function checkSession() {
    const requestGeneration = ++generation;
    try {
      const response = await api<{ user: AppUser }>('/api/v1/auth/me');
      if (requestGeneration === generation) currentUser.value = response.user;
    } catch { if (requestGeneration === generation) currentUser.value = null; }
    if (requestGeneration === generation) authReady.value = true;
  }
  async function signIn() {
    const requestGeneration = ++generation;
    loginError.value = '';
    loginPending.value = true;
    try {
      const response = await api<{ user: AppUser }>('/api/v1/auth/login', { method: 'POST', body: JSON.stringify(loginForm) });
      if (requestGeneration !== generation) return;
      currentUser.value = response.user;
      await options.initializeWorkspace();
      if (requestGeneration !== generation) options.clearWorkspace();
    } catch (cause) { if (requestGeneration === generation) loginError.value = cause instanceof Error ? cause.message : 'Anmeldung nicht möglich.'; }
    finally { if (requestGeneration === generation) loginPending.value = false; }
  }
  async function signOut() {
    generation += 1;
    loginPending.value = false;
    options.clearWorkspace();
    currentUser.value = null;
    usersOpen.value = false;
    managedUsers.value = [];
    await api('/api/v1/auth/logout', { method: 'POST' }).catch(() => undefined);
  }
  async function openUserManagement() {
    if (currentUser.value?.role !== 'admin') return;
    const requestGeneration = generation;
    userManagementError.value = '';
    try {
      const response = await api<{ users: Array<AppUser & { createdAt?: string }> }>('/api/v1/auth/users');
      if (requestGeneration !== generation) return;
      managedUsers.value = response.users;
      usersOpen.value = true;
    } catch (cause) { if (requestGeneration === generation) userManagementError.value = cause instanceof Error ? cause.message : 'Benutzer konnten nicht geladen werden.'; }
  }
  async function createUser() {
    if (currentUser.value?.role !== 'admin') return;
    const requestGeneration = generation;
    userManagementError.value = '';
    try {
      const response = await api<{ user: AppUser }>('/api/v1/auth/users', { method: 'POST', body: JSON.stringify(newUserForm) });
      if (requestGeneration !== generation) return;
      managedUsers.value.push(response.user);
      newUserForm.username = ''; newUserForm.password = ''; newUserForm.role = 'member';
    } catch (cause) { if (requestGeneration === generation) userManagementError.value = cause instanceof Error ? cause.message : 'Benutzer konnte nicht angelegt werden.'; }
  }
  return { authReady, currentUser, loginForm, loginError, loginPending, usersOpen, managedUsers, newUserForm, userManagementError, checkSession, signIn, signOut, openUserManagement, createUser };
}
